"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  name: "Cape Coral",
  description: "Our central headquarters. Operating from the heart of Cape Coral allows us to deploy rapidly across the entire region.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_waterfront_landscape.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_waterfront_landscape.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_waterfront_landscape.png-wvc-sizes",
  isHub: true
}, {
  name: "Punta Gorda",
  description: "Extending our trusted services north, bringing Cape Coral quality to the Punta Gorda community.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_harbor_view.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_harbor_view.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_harbor_view.png-wvc-sizes",
  isHub: false
}, {
  name: "Port Charlotte",
  description: "Seamless coverage across the bridge, ensuring Port Charlotte residents receive timely expert care.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_coastal_nature.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_coastal_nature.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_coastal_nature.png-wvc-sizes",
  isHub: false
}, {
  name: "Burnt Store",
  description: "Dedicated routes covering the entire Burnt Store corridor directly from our main base.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/burnt_store_marina_landscape.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/burnt_store_marina_landscape.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/burnt_store_marina_landscape.png-wvc-sizes",
  isHub: false
}, {
  name: "North Fort Myers",
  description: "Natural expansion into neighboring North Fort Myers, providing comprehensive regional support.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_river_scenery.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_river_scenery.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_river_scenery.png-wvc-sizes",
  isHub: false
}, {
  name: "Arcadia",
  description: "Providing reliable pest protection to the Arcadia area, ensuring rural and residential properties stay pest-free.",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_rural_florida_landscape.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_rural_florida_landscape.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_rural_florida_landscape.png-wvc-sizes",
  isHub: false
}];
export default function ServiceCoverageFromCapeCoral() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="service-coverage-from-cape-coral" className="w-full py-16 md:py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerReveal.ref} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4 border-foreground/20 text-foreground bg-background/50 backdrop-blur-sm">
            <Navigation className="w-3 h-3 mr-1" />
            Regional Coverage
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight mb-6">
            Pest Control Service Areas Near Cape Coral, FL
          </h2>
          <p className="text-lg md:text-xl text-foreground/80 font-default max-w-2xl mb-6">
            Operating from our Cape Coral base, we proudly serve the surrounding communities with expert pest management. From our central hub, we've naturally expanded our reach to ensure our neighbors receive the same top-tier, rapid-response service.
          </p>
          <div className="text-base md:text-lg text-foreground/70 font-default max-w-2xl space-y-2">
            <p>
              Our expert technicians provide comprehensive protection across <Link to="/PuntaGordaPestControlPage" className="text-primary hover:underline font-semibold">Punta Gorda</Link>, 
              reliable solutions in <Link to="/PortCharlottePestControlPage" className="text-primary hover:underline font-semibold">Port Charlotte</Link>, 
              dedicated support for <Link to="/NorthFortMyersPestControlPage" className="text-primary hover:underline font-semibold">North Fort Myers</Link>, 
              and specialized care for the <Link to="/ArcadiaPestControlPage" className="text-primary hover:underline font-semibold">Arcadia</Link> community.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {locations.map((location, i) => {
          const cardReveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          return <div key={location.name} data-index={i} ref={cardReveal.ref} className={`transition-all duration-700 ease-out ${cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className={`h-full overflow-hidden flex flex-col border-border/10 shadow-lg hover:shadow-xl transition-shadow duration-300 ${location.isHub ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}>
                  <div className="relative w-full">
                    <AspectRatio ratio={16 / 9}>
                      <img src={location.src} data-wvc-srcset={location.srcset} data-wvc-sizes={location.sizes} alt={`Service area in ${location.name}`} className="object-cover w-full h-full" loading="lazy" />
                    </AspectRatio>
                    {location.isHub && <div className="absolute top-4 right-4">
                        <Badge variant="default" className="shadow-md font-default">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Central Hub
                        </Badge>
                      </div>}
                  </div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className={`w-5 h-5 ${location.isHub ? 'text-primary' : 'text-muted-foreground'}`} />
                      <CardTitle className="text-xl md:text-2xl font-default">
                        {location.name}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <CardDescription className="text-base text-card-foreground/80 font-default">
                      {location.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" className="font-default text-lg px-8 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
            <Link to="/ContactPage">
              Check Availability in Your Area
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}